package ui.location


import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.focus.*
import androidx.compose.ui.unit.*
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import kotlinx.coroutines.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.location.detail.*
import ui.widget.*


data class LocationListingArgs(
    var companyId: Long = -1,
    var showAll: Boolean = false
)

@ComposableNavNode(LocationListingArgs::class)
@Composable
fun LocationListingUI() {
    with(LocationListingScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun LocationListingScope.Content(nnvl: LocationListingScope.Wrapper) {

    val stateHorizontal = rememberScrollState(0)
    val itemMeasurer = headerSpecs.rememberMeasurer()

    val itemCreatorState = DialogState.remember()
    ItemCreatorDialog(itemCreatorState)
    val scrollState = nav.rememberLazyListState("locations-${args.companyId}")

    Column(Modifier.fillMaxSize().padding(top = 8.dp).padding(start = 8.dp)) {
        HomeSearchUI(vm.searchFiled, label = Res.string.location_listing__search)
        Space(8.dp)
        Box(
            Modifier.fillMaxSize()
        ) {
            Box(
                Modifier.fillMaxSize()
                    .tableMeasurer(itemMeasurer)
                    .horizontalScroll(stateHorizontal)
            ) {
                LazyColumn(
                    state = scrollState,
                    contentPadding = PaddingValues(end = 8.dp),
                    modifier = Modifier.fillMaxHeight()
                        .width(itemMeasurer.rowWidth)
                ) {
                    stickyHeader {
                        ListingHeader(headerSpecs)
                    }
                    itemsIndexed(
                        SearchWrapper.use(
                            fields = arrayOf(
                                Io.ListingLocation.Item::id,
                                Io.ListingLocation.Item::idHex,
                                Io.ListingLocation.Item::name,
                                Io.ListingLocation.Item::name_company
                            ),
                            items = nnvl.state,
                            query = vm.searchFiled,
                            sort = compareByDescending {
                                if (it.data.id < 1000) Long.MAX_VALUE - it.data.id else it.data.id
                            }
                        )) { _, item ->

                        ListingDivider()
                        ListingItem(
                            item = item,
                            specs = headerSpecs,
                            background = if (item.data.emergencyActive) MaterialTheme.colorScheme.errorContainer
                            else MaterialTheme.colorScheme.surfaceContainer,
                        ) {
                            nav.findRoot().navTo_LocationUI(
                                LocationArgs(
                                    companyId = item.data.id_company,
                                    locationId = item.data.id
                                )
                            )
                        }
                    }
                    ListingFooter()
                }
            }
            HorizontalScrollbar(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(bottom = 8.dp)
                    .fillMaxWidth(),
                adapter = rememberScrollbarAdapter(stateHorizontal)
            )
            if (nnvl.canAdd && !args.showAll) {
                FloatingActionButton(
                    onClick = { itemCreatorState.show() },
                    modifier = Modifier.align(Alignment.BottomEnd)
                        .padding(24.dp)
                ) {
                    Icon(Svg.Plus, null)
                }
            }

        }
    }
}

@Composable
fun LocationListingScope.ItemCreatorDialog(state: DialogState) {
    Dialog(state) {
        val item = remember {
            LocationListingScope.Wrapper.Item(
                Io.ListingLocation.Item.EMPTY
            )
        }
        val focus = remember {
            FocusRequester()
        }
        var canSave by remember { mutableStateOf(true) }
        DialogUI(
            onDismissRequest = { state.hide() },
            modifier = Modifier.align(Alignment.Center),
            confirmButton = {
                TextButton(
                    onClick = {
                        canSave = false
                        action(LocationListingVM.Action.New(item.data))
                        state.hide()
                    },
                    enabled = item.change && canSave
                ) { Text(Res.string.dialog__action_save.rememberString()) }
            },
            dismissButton = {
                TextButton(
                    onClick = { state.hide() }
                ) { Text(Res.string.dialog__action_cancel.rememberString()) }
            },

            title = { Text(Res.string.location_listing__new_title.rememberString()) }
        ) {
            TextField(
                value = item.name.text,
                modifier = Modifier.focusRequester(focus),
                leadingIcon = { Icon(Svg.Format_text, null) },
                placeholder = { Text(Res.string.location_listing__new_name_hint.rememberString()) },
                onValueChange = {
                    item.name.set(it)
                })
        }
        LaunchedEffect(Unit) {
            delay(100)
            focus.requestFocus()
        }
    }
}


